import * as Yup from 'yup';
import dayjs from 'dayjs';

export const productFields = [
  {
    label: 'Price GBP',
    name: 'price_gbp',
    type: 'numberFormat',
    schema: Yup.number().typeError('The price must be a number'),
  },
  {
    label: 'Price GBP Date',
    name: 'price_gbp_date',
    type: 'date',
    schema: Yup.date().typeError('Must be a valid date'),
  },
  {
    label: 'Price Euro',
    name: 'price_euro',
    type: 'numberFormat',
    schema: Yup.number().typeError('The price must be a number'),
  },
  {
    label: 'Price Euro date',
    name: 'price_euro_date',
    type: 'date',
    schema: Yup.date().typeError('Must be a valid date'),
  },
  {
    label: 'Dial Color',
    name: 'dial_color',
    schema: Yup.string(),
  },
  {
    label: 'Case Material',
    name: 'case_material',
    schema: Yup.string(),
  },
  {
    label: 'Case Diameter',
    name: 'case_diameter',
    schema: Yup.string(),
  },
  {
    label: 'Case Glass',
    name: 'case_glass',
    schema: Yup.string(),
  },
  {
    label: 'Case Back',
    name: 'case_back',
    schema: Yup.string(),
  },
  {
    label: 'Water Resistance',
    name: 'case_water',
    schema: Yup.string(),
  },
  {
    // Optional, like every other attribute here: a bare Yup.string() has no
    // .required(), so the form submits fine when they are left blank, and a
    // product with no value simply does not render the row (see addField in
    // views/Listing/Specification.jsx).
    label: 'Bracelet Type',
    name: 'bracelet_type',
    schema: Yup.string(),
  },
  {
    label: 'Clasp Type',
    name: 'clasp_type',
    schema: Yup.string(),
  },
  {
    label: 'Title',
    name: 'title',
    schema: Yup.string(),
  },
  {
    label: 'Limitation',
    name: 'limitation',
    schema: Yup.string(),
  },
  {
    label: 'Year',
    name: 'production_year',
    schema: Yup.number().typeError('Must be a number')
      .max(dayjs().year(), 'The year can\'t be in the future')
      .min(dayjs().year() - 100, 'It can\'t be that old'),
  },
  {
    label: 'Reference',
    name: 'movement_reference',
    schema: Yup.string(),
  },
  {
    label: 'Movement: Quartz / Automatic',
    name: 'movement',
    schema: Yup.string(),
  },
  {
    label: 'Additional Info',
    name: 'movement_info',
    schema: Yup.string(),
  },
  {
    label: 'Power Reserve',
    name: 'power_reserve',
    schema: Yup.string(),
  },
  {
    label: 'Jewels',
    name: 'movement_jewels',
    schema: Yup.string(),
  },
  // One free-text field for the details that set a vintage piece apart and
  // have no fixed attribute of their own — "honey patina dial, unpolished
  // case, original bracelet with stamped clasp".
  //
  // Named for vintage rather than generically ("Additional Details", as it
  // first shipped) because `movement_info` above is already labelled
  // "Additional Info": two "Additional …" rows in the same specification panel
  // gave whoever was filling the form in nothing to choose between.
  //
  // Last in the list, after the fixed attributes it supplements. No length
  // cap: the backend routes anything over 255 chars into
  // product_values.long_value. Left empty it writes no row at all, so it
  // renders nowhere.
  {
    label: 'Vintage Details',
    name: 'vintage_details',
    type: 'textarea',
    schema: Yup.string(),
  },
];

export const formConfig = {
  brand: {
    label: 'Brand',
    placeholder: 'Select Brand',
    name: 'brand_id',
    type: 'select',
    optionsUrl: categoryId => `/api/categories/${categoryId}/brands?all=true`,
    schema: Yup.number().integer().required('Required'),
  },
  model: {
    label: 'Model',
    placeholder: 'Select Model',
    name: 'model_id',
    type: 'select',
    optionsUrl: (categoryId, brandId) => `/api/categories/${categoryId}/brands/${brandId}/models`,
    schema: Yup.number().integer().required('Required'),
  },
  referenceNumber: {
    label: 'Reference Number',
    name: 'reference_number',
    schema: Yup.string().required('Required'),
  },
  images: {
    label: 'Upload images',
    name: 'images',
    type: 'galleryUpload',
    schema: Yup.array().required().min(1, 'At least one image is required'),
  },
  productFields,
};

// Array-typed fields must start as [] (not '') — an empty-string seed crashed
// the gallery's `.map` and blanked the page. Default by field type.
const emptyValueFor = (type) =>
  type === 'galleryUpload' || type === 'multiselect' ? [] : '';

const extractInfo = (obj, infoField = null) => Object.values(obj)
  .reduce((values, config) => {
    if (Array.isArray(config)) {
      const fields = config.reduce((fieldValues, field) => ({
        ...fieldValues,
        [field.name]: infoField ? field[infoField] : emptyValueFor(field.type),
      }), {});

      return { ...values, ...fields };
    }

    return { ...values, [config.name]: infoField ? config[infoField] : emptyValueFor(config.type) };
  }, {});

export const initialValues = extractInfo(formConfig);

export const validationSchema = Yup.object().shape(extractInfo(formConfig, 'schema'));

/**
 * Seed the form from an existing submission (GET /api/store-catalog/products/{id})
 * for the edit-until-approved flow. Prices are STORED in minor units (pence) and
 * the form multiplies by 100 on submit, so divide here to show major units.
 */
export const initialValuesFromSubmission = (product) => {
  const values = product?.values || {};

  const fieldValues = productFields.reduce((acc, field) => {
    let value = values[field.name] ?? '';
    if ((field.name === 'price_gbp' || field.name === 'price_euro') && value !== '') {
      value = String(Math.round(parseInt(value, 10) / 100));
    }
    return { ...acc, [field.name]: value === null ? '' : value };
  }, {});

  return {
    ...initialValues,
    ...fieldValues,
    brand_id: product?.brand_id ?? null,
    model_id: product?.model_id ?? null,
    reference_number: product?.reference_number ?? '',
    images: (product?.images || []).map(Number),
  };
};
